// Shared label resolvers for translated pills/badges
// Usage: const { stageLabel, genderLabel, statusLabel } = useLabels();

import { useI18n, type Translations } from './index';
import { roleLabelOf, genderLabelOf } from '../lib/fillTemplate';

/** #531 — a count in the plural form of the language. Lithuanian has three
 *  forms (1 kova, 2 kovos, 10 kovų) and English two; `Intl.PluralRules`
 *  picks the category ('one', 'few', 'other'). `{n}` is the count. */
export function countLabel(lang: string, n: number, forms: { one: string; few: string; other: string }): string {
  const category = new Intl.PluralRules(lang === 'lt' ? 'lt' : 'en').select(n);
  const form = category === 'one' ? forms.one : category === 'few' ? forms.few : forms.other;
  return form.replace('{n}', String(n));
}

/** #559 — the text of a success answer in the language of the page: the
 *  answer names a `code`, and `t.success.<CODE>` holds the text (with the
 *  three count forms when it has `{n}`). The page never shows the English
 *  `message` of the answer: an unknown code gives "Done ✓". */
export function successText(data: { code?: string; params?: Record<string, string | number | null> } | undefined): string {
  const { t, lang } = useI18n.getState();
  const texts = t.success as Record<string, string | { one: string; few: string; other: string }>;
  const entry = data?.code ? texts[data.code] : undefined;
  if (!entry) return t.success.DONE;
  const params = data?.params ?? {};
  let text = typeof entry === 'string' ? entry : countLabel(lang, Number(params.n ?? 0), entry);
  for (const [key, value] of Object.entries(params)) text = text.split(`{${key}}`).join(String(value ?? ''));
  return text;
}

/** The name of a bracket stage. A plain function: the ring screens give
 *  the English dictionary, not the one of the store (#604, #608). */
export function stageLabel(t: Translations, s: string): string {
  const names: Record<string, string> = { QF: t.common.quarterfinal, SF: t.common.semifinal, Final: t.common.final, R16: t.common.round16, R32: t.common.round32, Challenge: t.common.challenge };
  return names[s] || s;
}

/** #531 — the gender of a DIVISION: a bout, a bracket, a group of
 *  registrations, a filter. Lithuanian needs the plural here ("Vyrai",
 *  "Moterys"); the singular of genderLabel() read as one person. */
export function divisionLabel(t: Translations, g: string): string {
  return g?.toLowerCase() === 'male' ? t.common.maleDivision : g?.toLowerCase() === 'female' ? t.common.femaleDivision : (g || '');
}

/** #661 — the name of a country in the language of the dictionary: "Lietuva",
 *  "Lithuania". A code that the dictionary does not know shows as the code. A
 *  plain function, like stageLabel(): a component that sorts or builds a list
 *  inside `useMemo` gives it the dictionary. This is the ONE place that reads
 *  `t.country.names` (`dry-labels-661.spec.ts`). */
export function countryName(t: Translations, code: string): string {
  return (t.country.names as Record<string, string>)[code] ?? code;
}

export function useLabels() {
  const { t, lang } = useI18n();

  /** #531 — "5 bouts" / "5 kovos", "10 kovų", "21 kova". */
  const boutCount = (n: number) =>
    countLabel(lang, n, { one: t.common.boutsOne, few: t.common.boutsFew, other: t.common.boutsOther });

  /** #663 — the count under a filter bar: "1 result" / "1 rezultatas", "2 rezultatai", "10 rezultatų". */
  const resultCount = (n: number) =>
    countLabel(lang, n, { one: t.common.resultsOne, few: t.common.resultsFew, other: t.common.resultsOther });

  /** #663 — the count of audit entries: "1 entry" / "1 įrašas", "2 įrašai", "10 įrašų". */
  const entryCount = (n: number) =>
    countLabel(lang, n, { one: t.common.entriesOne, few: t.common.entriesFew, other: t.common.entriesOther });

  /** #552 — the button of a modal that creates bouts: "Create 1 Bout" /
   *  "Sukurti 1 kovą", "Sukurti 3 kovas", "Sukurti 10 kovų". */
  const createBoutsLabel = (n: number) =>
    countLabel(lang, n, { one: t.common.createBoutsOne, few: t.common.createBoutsFew, other: t.common.createBoutsOther });

  /** #531 — the bracket counts: "8 boxers" / "8 kovotojai", "12 kovotojų", "21 kovotojas". */
  const fighterCount = (n: number) =>
    countLabel(lang, n, { one: t.common.fightersOne, few: t.common.fightersFew, other: t.common.fightersOther });

  /** #551 — the registered boxers on a tournament card: "12 boxers" / "12 kovotojų". */
  const boxerCount = (n: number) =>
    countLabel(lang, n, { one: t.common.boxersOne, few: t.common.boxersFew, other: t.common.boxersOther });

  /** #531 — the athletes of a team roster (the Teams page): "12 sportininkų". The Check-in
   *  tab counts boxers (`boxerCount`, #554). */
  const athleteCount = (n: number) =>
    countLabel(lang, n, { one: t.checkin.athletesOne, few: t.checkin.athletesFew, other: t.checkin.athletesOther });
  /** #663 — the teams of the Teams page and of the Athletes header: "1 team" / "1 komanda", "2 komandos", "10 komandų". */
  const teamCount = (n: number) =>
    countLabel(lang, n, { one: t.teams.teamsOne, few: t.teams.teamsFew, other: t.teams.teamsOther });
  const readyCount = (n: number) =>
    countLabel(lang, n, { one: t.checkin.readyOne, few: t.checkin.readyFew, other: t.checkin.readyOther });

  /** #531 — a role id as a person reads it: "head_coach" → "Head Coach" /
   *  "Vyr. treneris". The raw id showed in English on a Lithuanian page. */
  const roleLabel = (r: string) => roleLabelOf(t, r);

  /** #531 — an age in a compact line: "25 y" / "25 m.". */
  const ageShort = (n: number) => t.common.ageShort.replace('{n}', String(n));

  /** The gender of ONE person (an athlete in a form, a profile). */
  const genderLabel = (g: string) => genderLabelOf(t, g);

  /** #531 — the initial of the short badge. The English "M" / "F" read as
   *  "moteris" (woman) in Lithuanian for a MALE athlete. */
  const genderShort = (g: string) =>
    g?.toLowerCase() === 'male' ? t.common.maleShort : g?.toLowerCase() === 'female' ? t.common.femaleShort : '';

  const statusLabel = (s: string) =>
    ({ pending: t.common.pending, current: t.common.live, scored: t.common.scored }[s] || s);

  /** #661 — the status of a request (a role, a claim) as a badge and a filter
   *  name it: "Approved" / "Patvirtinta". A badge says "Pending approval"; a
   *  filter button says "Pending" (`short`). An unknown status stays as it is. */
  const requestStatusLabel = (s: string, short = false) =>
    ({ pending: short ? t.common.pending : t.profile.pendingApproval, approved: t.profile.approved, rejected: t.profile.rejected }[s] || s);

  /** #661 — a country code as a person reads it: "LT" → "Lietuva" / "Lithuania". */
  const countryNameOf = (code: string) => countryName(t, code);

  /** #536 — the type of a tournament as the card and the form name it:
   *  "Championship", "Open Ring", "Championship + Open Ring". */
  const typeLabel = (type: string) =>
    ({ championship: t.tournament.championship, open_ring: t.tournament.openRing, hybrid: t.tournament.hybrid }[type] || type);

  return {
    stageLabel: (s: string) => stageLabel(t, s), genderLabel, divisionLabel: (g: string) => divisionLabel(t, g), genderShort, boutCount, resultCount, entryCount, createBoutsLabel, fighterCount, boxerCount, athleteCount, teamCount, readyCount, roleLabel, ageShort, statusLabel, requestStatusLabel, countryName: countryNameOf, typeLabel, t };
}

/** #458 — the winner of a verdict as the verdict texts name it: the corner
 *  name in capitals (RED, RAUDONAS), or DRAW. A plain function, so a socket
 *  handler can call it with the store's `t`. An unknown value stays as it is. */
export function winnerLabel(t: Translations, winner: string): string {
  const names: Record<string, string> = { red: t.common.red, blue: t.common.blue, draw: t.common.draw };
  return (names[winner] ?? winner).toUpperCase();
}
